import Button from "@mui/material/Button";
import DialogContentText from "@mui/material/DialogContentText";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { Dialog } from "../Dialog";
import { ProgressBar, type ProgressCount } from "../ProgressBar";

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  confirmColor?: "error" | "warning" | "primary";
  loading?: boolean;
  /** Shown inside the dialog when the action fails — keeps the dialog open. */
  error?: string | null;
  /** Shown as a progress bar while `loading`, for an action that works through several items. */
  progress?: ProgressCount | null;
  onConfirm: () => void;
  onCancel: () => void;
}

const ProgressSlot = styled("div")({
  marginTop: "16px",
});

const ErrorMessage = styled(Typography)({
  marginTop: "12px",
});

/** Generic confirmation dialog. Pair with useConfirmHandler to gate a destructive action. */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  confirmColor = "primary",
  loading = false,
  error,
  progress,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title={title}
      closeLabel="Cancel"
      loading={loading}
      maxWidth="xs"
      actions={
        <Button variant="contained" color={confirmColor} onClick={onConfirm} disabled={loading}>
          {loading ? "…" : confirmLabel}
        </Button>
      }
    >
      <DialogContentText>{message}</DialogContentText>
      {loading && progress && (
        <ProgressSlot>
          <ProgressBar {...progress} />
        </ProgressSlot>
      )}
      {error && (
        <ErrorMessage variant="body2" color="error">
          {error}
        </ErrorMessage>
      )}
    </Dialog>
  );
}
